import flatten from 'lodash-es/flatten';
import uniq from 'lodash-es/uniq';
import { setSubmitFailed, setSubmitSucceeded, startSubmit } from 'redux-form';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import type Order from '~/types/Order';

import { ActionKeys } from '~/actions/constants';
import * as customsActions from '~/actions/customs.actions';
import * as ordersActions from '~/actions/orders.actions';
import { CUSTOMS_DECLARATION_FORM, CUSTOMS_ITEM_FORM } from '~/constants/form';
import { getBatchEntriesIdsToUpdate } from '~/sagas/batch-entry-utils';
import { getBatchEntryById } from '~/selectors/batch-entry.selectors';
import { getOrdersData } from '~/selectors/orders.selectors';
import * as customsService from '~/services/customs.service';
import * as customsDeclaration from '~/types/CustomsDeclaration';
import * as customsItem from '~/types/CustomsItem';
import {
  batchEntryExtendedFromJSON,
  type BatchEntryExtended,
  type JSONBatchEntriesResponse,
} from '~/types/OrderBatchEntry';
import { trackWithGlobalProps } from '~/utils/TrackingHelper/utils';
import { Events } from '~/utils/segment';

import { selectState } from '..';
import { synchronizeOrdersV2 } from '../orders/orders-v2-sync.util';

export function* createCustomsItem(action: ReturnType<typeof customsActions.createCustomsItem>) {
  try {
    yield put(startSubmit(CUSTOMS_ITEM_FORM));

    const { batchEntriesIdsToUpdate } = yield call(
      getBatchEntriesIdsToUpdate,
      action.payload.orderSelection,
    );

    const ordersData: ReturnType<typeof getOrdersData> =
      yield selectState<ReturnType<typeof getOrdersData>>(getOrdersData);
    const order: Order = ordersData[Object.keys(action.payload.orderSelection)[0]];

    const result: JSONBatchEntriesResponse = yield call(
      customsService.createCustomsItem,
      uniq(batchEntriesIdsToUpdate),
      [customsItem.payloadToJSON(action.payload.customsItemPayload)],
    );
    yield call(synchronizeOrdersV2, {
      orderObjectIds: result.data.map((batchEntry) => batchEntry.order_object_id),
    });

    const parsedBatchEntries = result?.data.map(batchEntryExtendedFromJSON) || [];

    yield put(ordersActions.fetchOrder(order.objectId));

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));
    yield put(ordersActions.startBatchEntriesPolling([order.objectId]));

    yield put(customsActions.createCustomsItemSuccess());
    yield put(setSubmitSucceeded(CUSTOMS_ITEM_FORM));
  } catch (err) {
    yield put(setSubmitFailed(CUSTOMS_ITEM_FORM));
    yield put(customsActions.createCustomsItemError(err));
  }
}

export function* editCustomsItem(action: ReturnType<typeof customsActions.editCustomsItem>) {
  try {
    yield put(startSubmit(CUSTOMS_ITEM_FORM));

    const { batchEntriesIdsToUpdate, resetBatchEntries } = yield call(
      getBatchEntriesIdsToUpdate,
      action.payload.orderSelection,
    );

    // If this is a batch entry repurchase, the batch entry is reset meaning that new customs items
    // will be generated (based off of existing batch entry)
    // we need to check the reset batch entry and replace the customsitem id with the regenerated customs item
    let initialCustomsItems: customsItem.CustomsItem[];
    let customsItemPayload: customsItem.CustomsItemPayload = {
      ...action.payload.customsItemPayload,
    };
    if (resetBatchEntries.length > 0) {
      initialCustomsItems = resetBatchEntries.reduce(
        (customsItems: customsItem.CustomsItem[], batchEntry: BatchEntryExtended) => [
          ...customsItems,
          ...(batchEntry.customsItems as customsItem.CustomsItem[]),
        ],
        [],
      );

      // Find initial customsItem
      for (const ci of initialCustomsItems) {
        if (customsItem.isEqualToPayload(ci, action.payload.initialValues)) {
          customsItemPayload = {
            ...customsItemPayload,
            id: ci.id,
          };
          break;
        }
      }
    }

    const ordersData: ReturnType<typeof getOrdersData> =
      yield selectState<ReturnType<typeof getOrdersData>>(getOrdersData);

    const order: Order = ordersData[Object.keys(action.payload.orderSelection)[0]];
    yield call(trackWithGlobalProps, action.payload.appLocation, Events.customs_items_form_submit, {
      order_id: order.objectId,
      ...action.payload.trackingData,
      ...customsItem.payloadToJSON(customsItemPayload),
    });

    const result: JSONBatchEntriesResponse = yield call(
      customsService.updateCustomsItem,
      uniq(batchEntriesIdsToUpdate),
      customsItem.payloadToJSON(customsItemPayload),
    );
    yield call(synchronizeOrdersV2, {
      orderObjectIds: result.data.map((batchEntry) => batchEntry.order_object_id),
    });

    const parsedBatchEntries = result?.data.map(batchEntryExtendedFromJSON) || [];

    yield put(ordersActions.fetchOrder(order.objectId));

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));
    yield put(ordersActions.startBatchEntriesPolling(Object.keys(action.payload.orderSelection)));

    yield put(customsActions.editCustomsItemSuccess());

    yield put(setSubmitSucceeded(CUSTOMS_ITEM_FORM));
  } catch (err) {
    yield put(setSubmitFailed(CUSTOMS_ITEM_FORM));
    yield put(customsActions.editCustomsItemError(err));
  }
}

interface ReturnTypeBatchEntriesToUpdate {
  batchEntriesIdsToUpdate: number[];
  resetBatchEntries: BatchEntryExtended[];
}

export function* deleteCustomsItem(action: ReturnType<typeof customsActions.deleteCustomsItem>) {
  try {
    const { initialValues, orderSelection } = action.payload;
    const { batchEntriesIdsToUpdate, resetBatchEntries }: ReturnTypeBatchEntriesToUpdate =
      yield call(getBatchEntriesIdsToUpdate, orderSelection);

    let initialCustomsItems: customsItem.CustomsItem[];
    let customsItemsPayload: customsItem.CustomsItemPayload[] = [...initialValues];

    // If no batch entry id in order selection, then there was no batch entry prior to the delete call
    // so we get the customs items from the newly created batch entry
    if (flatten(Object.values(orderSelection)).length < 1) {
      const batchEntry: ReturnType<typeof getBatchEntryById> = yield select(getBatchEntryById, {
        batchEntryId: batchEntriesIdsToUpdate[0],
      });
      customsItemsPayload = batchEntry?.customsItems?.map(customsItem.customsItemToPayload) || [];
    }

    // If this is a batch entry repurchase, the batch entry is reset meaning that new customs items
    // will be generated (based off of existing batch entry)
    // we need to check the reset batch entry and replace the customsitem id with the regenerated customs item
    if (resetBatchEntries.length > 0) {
      initialCustomsItems = resetBatchEntries.reduce(
        (customsItems: customsItem.CustomsItem[], batchEntry: BatchEntryExtended) => [
          ...customsItems,
          ...(batchEntry.customsItems as customsItem.CustomsItem[]),
        ],
        [],
      );

      // Find initial customsItem
      customsItemsPayload.forEach((payload, index) => {
        for (const ci of initialCustomsItems) {
          if (customsItem.isEqualToPayload(ci, payload)) {
            customsItemsPayload[index].id = ci.id;
            break;
          }
        }
      });
    }

    const result: JSONBatchEntriesResponse = yield call(
      customsService.deleteCustomsItems,
      uniq(batchEntriesIdsToUpdate),
      customsItemsPayload.map(customsItem.payloadToJSON),
    );
    yield call(synchronizeOrdersV2, {
      orderObjectIds: result.data.map((batchEntry) => batchEntry.order_object_id),
    });

    const parsedBatchEntries = result?.data.map(batchEntryExtendedFromJSON) || [];

    yield put(ordersActions.fetchOrder(Object.keys(action.payload.orderSelection)[0]));

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));
    yield put(ordersActions.startBatchEntriesPolling(Object.keys(action.payload.orderSelection)));
    yield put(customsActions.deleteCustomsItemSuccess());
  } catch (_) {
    yield put(customsActions.deleteCustomsItemError('Something happened. Please try again'));
  }
}

export function* editCustomsDeclaration(
  action: ReturnType<typeof customsActions.editCustomsDeclaration>,
) {
  try {
    yield put(startSubmit(CUSTOMS_DECLARATION_FORM));

    const { batchEntriesIdsToUpdate, resetBatchEntries } = yield call(
      getBatchEntriesIdsToUpdate,
      action.payload.orderSelection,
    );

    // If this is a batch entry repurchase, the batch entry is reset meaning that new customs declaration
    // will be generated (based off of existing batch entry)
    // we need to check the reset batch entry and replace the declaration id  in payload with the regenerated one
    let initialCustomsDeclaration: customsDeclaration.CustomsDeclaration;
    let customsDeclarationPayload: customsDeclaration.CustomsDeclarationPayload = {
      ...action.payload.customsDeclarationPayload,
    };

    if (resetBatchEntries?.[0]?.customsDeclaration) {
      initialCustomsDeclaration = resetBatchEntries[0].customsDeclaration;

      if (
        customsDeclaration.isDeclarationEqual(initialCustomsDeclaration, {
          ...action.payload.initialValues,
          b13aFilingOption: action.payload.initialValues.b13aFilingOption?.value?.toString() || '',
          contentsExplanation: action.payload.initialValues.contentsExplanation,
          contentsType: action.payload.initialValues.contentsType.value.toString(),
          eelPfc: action.payload.initialValues.eelPfc
            ? action.payload.initialValues.eelPfc.value.toString()
            : '',
          incoterm: action.payload.initialValues.incoterm.value.toString(),
          nonDeliveryOption: action.payload.initialValues.nonDeliveryOption.value.toString(),
          taxIdType: action.payload.initialValues.taxIdType?.value?.toString() || '',
        }) ||
        initialCustomsDeclaration.id !== action.payload.customsDeclarationPayload.id
      ) {
        customsDeclarationPayload = {
          ...customsDeclarationPayload,
          id: initialCustomsDeclaration.id,
        };
      }
    }
    const orderIDs = Object.keys(action.payload.orderSelection);
    yield call(
      trackWithGlobalProps,
      action.payload.appLocation,
      Events.customs_options_form_submit,
      {
        order_id: orderIDs.length ? orderIDs[0] : '',
        ...action.payload.trackingData,
        ...customsDeclaration.prepareCustomsDeclarationSegmentData(
          customsDeclaration.toJSONCustomsDeclarationPayload(customsDeclarationPayload),
        ),
      },
    );
    const result: JSONBatchEntriesResponse = yield call(
      customsService.updateCustomsDeclaration,
      uniq(batchEntriesIdsToUpdate),
      customsDeclaration.toJSONCustomsDeclarationPayload(customsDeclarationPayload),
    );
    yield call(synchronizeOrdersV2, {
      orderObjectIds: result.data.map((batchEntry) => batchEntry.order_object_id),
    });

    const parsedBatchEntries = result?.data.map(batchEntryExtendedFromJSON) || [];

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));

    // Start polling the batch entries
    yield put(
      ordersActions.startBatchEntriesPolling(
        parsedBatchEntries.map((batchEntry) => batchEntry.orderObjectId),
      ),
    );

    yield put(customsActions.editCustomsDeclarationSuccess());

    yield put(setSubmitSucceeded(CUSTOMS_DECLARATION_FORM));
  } catch (err) {
    yield put(setSubmitFailed(CUSTOMS_DECLARATION_FORM));
    yield put(customsActions.editCustomsDeclarationError(err));
  }
}

export default [
  takeLatest(ActionKeys.CUSTOMS_ITEM_CREATE, createCustomsItem),
  takeLatest(ActionKeys.CUSTOMS_ITEM_UPDATE, editCustomsItem),
  takeLatest(ActionKeys.CUSTOMS_ITEM_DELETE, deleteCustomsItem),
  takeLatest(ActionKeys.CUSTOMS_DECLARATION_UPDATE, editCustomsDeclaration),
];
